/**
 * HERMES · Court Visual Configuration
 * -------------------------------------
 * Mapa determinístico sigla → { color, label, fullName }.
 * O MESMO tribunal SEMPRE renderiza com a MESMA cor, em qualquer
 * módulo do HERMES.
 *
 * Regras de paleta:
 *  - harmoniza com o HERMES (grafite + dourado + cinza-quente);
 *  - cores institucionais (nunca neon, nunca infantil);
 *  - todas têm contraste AA sobre fundo branco/off-white;
 *  - identidade textual (sigla + negrito) NÃO depende só da cor.
 *
 * Fallback: qualquer tribunal não mapeado usa NEUTRAL_FALLBACK.
 */

export const NEUTRAL_FALLBACK = { color: "#334155", label: null, fullName: null };

// Cores institucionais — grafite, azul-marinho, oliva, vinho, terracota,
// cerúleo, castanho. Todas foscas, sem saturação exagerada.
export const COURT_VISUAL_CONFIG = Object.freeze({
  // Superiores
  STF:   { color: "#7A1B1B", fullName: "Supremo Tribunal Federal" },
  STJ:   { color: "#144E7E", fullName: "Superior Tribunal de Justiça" },
  TST:   { color: "#3F5A2A", fullName: "Tribunal Superior do Trabalho" },
  TSE:   { color: "#5A3E1A", fullName: "Tribunal Superior Eleitoral" },
  STM:   { color: "#4A2E5A", fullName: "Superior Tribunal Militar" },
  CNJ:   { color: "#333A3F", fullName: "Conselho Nacional de Justiça" },

  // Federais Regionais
  TRF1:  { color: "#0D6E63", fullName: "TRF 1ª Região" },
  TRF2:  { color: "#1E5AA6", fullName: "TRF 2ª Região" },
  TRF3:  { color: "#8A4A1F", fullName: "TRF 3ª Região" },
  TRF4:  { color: "#5C3E8B", fullName: "TRF 4ª Região" },
  TRF5:  { color: "#B8891F", fullName: "TRF 5ª Região" },
  TRF6:  { color: "#0B4B7A", fullName: "TRF 6ª Região" },

  // Trabalho (TRTs — usam matiz oliva/verde-musgo para diferenciar do resto)
  TRT1:  { color: "#3E5B27", fullName: "TRT 1ª Região" },
  TRT2:  { color: "#2E5030", fullName: "TRT 2ª Região" },
  TRT3:  { color: "#4A6B2A", fullName: "TRT 3ª Região" },
  TRT4:  { color: "#375F35", fullName: "TRT 4ª Região" },
  TRT5:  { color: "#5A6E1F", fullName: "TRT 5ª Região" },
  TRT6:  { color: "#3E5B3A", fullName: "TRT 6ª Região" },
  TRT7:  { color: "#4C6A2F", fullName: "TRT 7ª Região" },
  TRT8:  { color: "#3F5F26", fullName: "TRT 8ª Região" },
  TRT9:  { color: "#2F5B3F", fullName: "TRT 9ª Região" },
  TRT10: { color: "#496A38", fullName: "TRT 10ª Região" },
  TRT11: { color: "#3B5D2B", fullName: "TRT 11ª Região" },
  TRT12: { color: "#4A6D33", fullName: "TRT 12ª Região" },
  TRT13: { color: "#345C2A", fullName: "TRT 13ª Região" },
  TRT14: { color: "#526E30", fullName: "TRT 14ª Região" },
  TRT15: { color: "#3D6B2C", fullName: "TRT 15ª Região" },
  TRT16: { color: "#476031", fullName: "TRT 16ª Região" },
  TRT17: { color: "#3A5D27", fullName: "TRT 17ª Região" },
  TRT18: { color: "#4E6A30", fullName: "TRT 18ª Região" },
  TRT19: { color: "#3F602A", fullName: "TRT 19ª Região" },
  TRT20: { color: "#456729", fullName: "TRT 20ª Região" },
  TRT21: { color: "#3B5A30", fullName: "TRT 21ª Região" },
  TRT22: { color: "#4B6D2F", fullName: "TRT 22ª Região" },
  TRT23: { color: "#3E602C", fullName: "TRT 23ª Região" },
  TRT24: { color: "#516730", fullName: "TRT 24ª Região" },

  // Estaduais (matizes variados para escaneamento visual)
  TJMG:  { color: "#8B2E2E", fullName: "TJ Minas Gerais" },
  TJSP:  { color: "#1B4F72", fullName: "TJ São Paulo" },
  TJRJ:  { color: "#B85C1F", fullName: "TJ Rio de Janeiro" },
  TJRS:  { color: "#2C4F8A", fullName: "TJ Rio Grande do Sul" },
  TJPR:  { color: "#0F4C3A", fullName: "TJ Paraná" },
  TJSC:  { color: "#5A2F73", fullName: "TJ Santa Catarina" },
  TJBA:  { color: "#8A5620", fullName: "TJ Bahia" },
  TJDF:  { color: "#374A6E", fullName: "TJ Distrito Federal" },
  TJGO:  { color: "#7B3B1E", fullName: "TJ Goiás" },
  TJPE:  { color: "#124E70", fullName: "TJ Pernambuco" },
  TJCE:  { color: "#8F4A1B", fullName: "TJ Ceará" },
  TJAM:  { color: "#0F5C3E", fullName: "TJ Amazonas" },
  TJPA:  { color: "#385F3D", fullName: "TJ Pará" },
  TJMT:  { color: "#4A6B27", fullName: "TJ Mato Grosso" },
  TJMS:  { color: "#5A3B2A", fullName: "TJ Mato Grosso do Sul" },
  TJES:  { color: "#7A5A1F", fullName: "TJ Espírito Santo" },
  TJMA:  { color: "#7A3B54", fullName: "TJ Maranhão" },
  TJPB:  { color: "#3E5C8A", fullName: "TJ Paraíba" },
  TJRN:  { color: "#6B3F27", fullName: "TJ Rio Grande do Norte" },
  TJAL:  { color: "#8B4F2A", fullName: "TJ Alagoas" },
  TJPI:  { color: "#5A4B27", fullName: "TJ Piauí" },
  TJSE:  { color: "#6E4A1F", fullName: "TJ Sergipe" },
  TJRO:  { color: "#4A5F27", fullName: "TJ Rondônia" },
  TJAC:  { color: "#2F5E4A", fullName: "TJ Acre" },
  TJAP:  { color: "#375D6E", fullName: "TJ Amapá" },
  TJRR:  { color: "#5A4A2A", fullName: "TJ Roraima" },
  TJTO:  { color: "#6B5220", fullName: "TJ Tocantins" },

  // Eleitorais (subgrupo — tom âmbar/oliva)
  TRE:   { color: "#6B4F1F", fullName: "TRE" },
});

/**
 * normalizeCourtCode(raw): sanitiza a sigla para chave do mapa.
 *  - remove espaços, pontos, hifens
 *  - uppercase
 *  - lida com formatos comuns ("TRT-3", "TRT 3ª", "TRT3", "TRT03")
 */
export function normalizeCourtCode(raw) {
  if (!raw) return null;
  const s = String(raw).toUpperCase().replace(/[\s.\-ª]/g, "");
  // Reduz "TRT03" → "TRT3", "TRF06" → "TRF6"
  const m = s.match(/^(TRT|TRF)0*(\d+)$/);
  if (m) return `${m[1]}${m[2]}`;
  return s;
}

/**
 * getCourtVisual(raw): retorna { code, color, label, fullName }.
 * Sempre retorna algo (fallback neutro).
 */
export function getCourtVisual(raw) {
  const code = normalizeCourtCode(raw);
  if (!code) return { code: "—", color: NEUTRAL_FALLBACK.color, label: raw || "—", fullName: null };
  const entry = COURT_VISUAL_CONFIG[code];
  if (entry) return { code, color: entry.color, label: code, fullName: entry.fullName };
  return { code, color: NEUTRAL_FALLBACK.color, label: code, fullName: null };
}
